import React, { useLayoutEffect, useState } from 'react';

import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { AnimateZoom } from './AnimateZoom';
import { Box } from './Box';
import { COMMON } from './constants';
import { Flex } from './Flex';
import { IconClose } from './Icons/Close';
import { Absolute } from './Position';
import theme from './theme';

type CloseReason = 'closeButtonWasClicked' | 'escKeyWasPressed' | 'backdropWasClicked';

type OnRequestClose = (ctxt: Partial<Record<CloseReason, boolean>>) => void;

type Props = {
  onRequestClose: OnRequestClose;
  showCloseButton?: boolean;
};

const useEscapeKey = (onEscapeKeyDown: () => void) => {
  React.useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      let isEscape = false;
      if ('key' in e) {
        isEscape = e.key === 'Escape' || e.key === 'Esc';
      } else {
        isEscape = (e as any).keyCode === 27;
      }
      if (isEscape) onEscapeKeyDown();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onEscapeKeyDown]);
};

function handleBackdropClick(e: React.MouseEvent, onRequestClose: OnRequestClose) {
  if (e.target !== e.currentTarget) return;
  onRequestClose({ backdropWasClicked: true });
}

const ModalOverlay = styled(Flex)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${theme.zIndices.modal};
  background: ${theme.colors.gray.mediumTranslucent};
`;

const Modal = styled(AnimateZoom)`
  border-radius: ${theme.radii[1]};
  position: relative;
  margin: 0 10px;
  max-height: calc(100% - 20px);
  overflow-y: auto;
  z-index: ${theme.zIndices.modal};
  -webkit-overflow-scrolling: touch;
`;

function createModalRootEl() {
  const elt = document.createElement('div');
  elt.setAttribute('data-qa', 'modal-root');
  document.body.appendChild(elt);
  return elt;
}

const ModalDialog = ({
  children,
  onRequestClose,
  showCloseButton,
  ...rest
}: React.PropsWithChildren<Props>): JSX.Element | null => {
  const [modalRootEl, setModalRootEl] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    // don't need to set element ID because we only have one `modalRootEl` instance for this component
    const elt = createModalRootEl();
    setModalRootEl(elt);
    return () => {
      document.body.removeChild(elt);
    };
  }, [setModalRootEl]);

  const modelEl = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    modelEl.current && disableBodyScroll(modelEl.current);
    return () => clearAllBodyScrollLocks();
  });

  useEscapeKey(() => onRequestClose({ escKeyWasPressed: true }));

  const onOverlayClick = React.useCallback((e: React.MouseEvent) => handleBackdropClick(e, onRequestClose), [
    onRequestClose,
  ]);
  const onCloseClick = React.useCallback(() => onRequestClose({ closeButtonWasClicked: true }), [onRequestClose]);

  if (!modalRootEl) return null;

  return ReactDOM.createPortal(
    <ModalOverlay justifyContent="center" alignItems="center" onClick={onOverlayClick}>
      <Modal bg="white" ref={modelEl} {...rest}>
        {showCloseButton && (
          <Absolute top="0" right="0" zIndex={theme.zIndices.modal}>
            <Box as="button" p="s" onClick={onCloseClick} aria-label="close" style={{ cursor: 'pointer' }}>
              <IconClose />
            </Box>
          </Absolute>
        )}
        <div>{children}</div>
      </Modal>
    </ModalOverlay>,
    modalRootEl,
  );
};

ModalDialog.defaultProps = {
  theme,
  showCloseButton: true,
};

ModalDialog.propTypes = {
  ...COMMON.propTypes,
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onRequestClose: PropTypes.func.isRequired,
  showCloseButton: PropTypes.bool,
};

ModalDialog.displayName = 'ModalDialog';

export { ModalDialog };
